/* eslint-disable sonarjs/cognitive-complexity */
import { ApolloClient } from '@apollo/client';
import { getOperationName } from '@apollo/client/utilities';
import fetch from 'node-fetch';
import logger from '@tcp/core/src/utils/loggerInstance';

import {
  isClient,
  isServer,
  getCurrentRouteName,
  getSiteId,
  getAPIConfig,
  getPlaceSiteId,
  updateCachedKeyMap,
  isSFCC,
} from '@tcp/core/src/utils';
import { getStoreRef } from '@tcp/core/src/utils/store.utils';
import { logout } from '@tcp/core/src/components/features/account/Logout/container/LogOut.actions';
import { resetCartCache } from '@tcp/core/src/components/features/CnC/BagPage/container/BagPage.actions';
import { getGraphQLErrorOnError, getGraphQLErrorOnSuccess } from './utils';
import { GRAPHQL_EXCEPTIONS } from '../../../../reduxStore/constants';
import { FETCH_POLICY } from './ApolloGraphQLConstants';
import { persistCacheOperations } from '../../../../utils/cache.util';
import { storeBuildNumber } from '../../../../utils/buildNumber.util';
import { getAPIErrorCode, getDateObject, getIsGuestUser } from '../../../../utils/utils';
import { toggleSiteMaintenanceBanner } from '../../../../reduxStore/actions';

if (!process.browser) {
  global.fetch = fetch;
}

/**
 * Class ApolloClientBase which extends ApolloClient
 */
class ApolloClientBase extends ApolloClient {
  constructor(options) {
    super({ ...options });
    if (isClient()) {
      persistCacheOperations();
      storeBuildNumber();
    }
  }

  /**
   * @function setCacheTimers
   * This method sets/updates the interval timers for clearing client cache
   * (Apollo store cache)
   *
   * @static
   * @memberof ApolloClientBase
   */
  setCacheTimers = () => {
    const apiConfig = getAPIConfig();
    let cacheInterval = 0;
    try {
      cacheInterval = parseInt(apiConfig.GRAPHQL_CLIENT_CACHE_CLEAR_INTERVAL, 10);
    } catch (e) {
      logger.error({
        error: e,
        extraData: {
          methodName: 'ApolloClientBase - setCacheTimers',
          cacheInterval: apiConfig.GRAPHQL_CLIENT_CACHE_CLEAR_INTERVAL,
        },
      });
      cacheInterval = 15;
    }
    if (isClient() && cacheInterval > 0) {
      if (this.clearStoreTimer) {
        clearInterval(this.clearStoreTimer);
      }

      this.clearStoreTimer = setInterval(async () => {
        await this.clearStore();
      }, cacheInterval * 60 * 1000); // cacheInterval is configured in minutes
    }
  };

  /**
   * Generate the path context based on contextual information such as
   * url for web, screen name for mobile
   *
   * Examples:
   * Web: layout-gym-us-web-home, seo-data-gym-us-web-help-center
   * Mobile: layout-gym-us-mobile-home, seo-data-gym-us-mobile-help-center
   *
   * @returns {string} Hyphen-separated path context (lower-cased)
   */
  getPathContext = (apiConfig, pageName) => {
    let pathContext = '';
    let splitPath = [];

    /**
     * Path Context for web consists of 2 parts:
     * 1: Site ID --> splitPath[1]
     * 2: Channel: "web"
     * 3. Page group (home, help-center, c, p, b, outfit etc.) --> splitPath[2]
     *
     * For Mobile - we are going with:
     * 1. Site ID
     * 2. Channel: "mobile"
     * 3. Route Name
     *
     * Page group/Route Name is the max level of granularity we are going with for cache tag path context
     */
    if (isServer()) {
      const { originalPath } = apiConfig;
      /* Web SSR Scenario */
      // Fix for DRP-5070 - do not use httpContext here as it gives wrong information when concurrent requests are processed
      splitPath = originalPath.split('/');
      pathContext = `${splitPath[1]}-web-${splitPath[2]}`;
    } else if (isClient()) {
      /* Web CSR Scenario */
      splitPath = window.location.pathname.split('/');
      // Incorrect cache tag generated for home when modules are pre fetched from PDP or bag page.
      if (pageName === 'home') {
        splitPath[2] = pageName;
      }
      pathContext = `${splitPath[1]}-web-${splitPath[2]}`;
    } else {
      /* Mobile App */
      pathContext = `${getSiteId()}-mobile-${getCurrentRouteName()}`;
    }

    return pathContext.toLowerCase();
  };

  /**
   * Executes query promise.
   *
   * @param {object} query - Query
   * @param {object} config - Config
   * @param {object} options - Options may contain {headers, fetchPolicy, variables, handleError}
   * @return {Promise} Query Execution Promise
   *
   */
  executeQueryPromise = (
    query,
    config,
    {
      headers,
      fetchPolicy,
      variables = {},
      handleError = false,
      newClient = false,
      context = {},
      callbackParams = {},
    },
    type = 'query',
    updateQuery,
    cacheConfigKey,
    TTL
  ) => {
    const headersWithAuth = {
      siteId: getPlaceSiteId(),
      platform: isSFCC() ? 'SF' : 'WC',
      ...headers,
    };
    const { enableGqlWarn, gqlWarnThreshold } = config;
    const rawQuery = query.loc.source.body;
    const { cache } = this;

    const isLogOutReq = (storeRef, errorCode, operationName) => {
      if (errorCode === 'MAINTENANCE_ERROR' && operationName !== 'GET_ALL_PROMOTIONS') {
        storeRef.dispatch(toggleSiteMaintenanceBanner(true));
      }

      return (
        ((getAPIErrorCode().length > 0 && getAPIErrorCode().includes(errorCode)) ||
          GRAPHQL_EXCEPTIONS.ERROR_MESSAGES.includes(errorCode) ||
          GRAPHQL_EXCEPTIONS.ERROR_CODES.includes(errorCode)) &&
        storeRef &&
        storeRef !== null &&
        !getIsGuestUser()
      );
    };

    return new Promise((resolve, reject) => {
      const startTime = getDateObject().getTime();
      const requestConfig = {
        variables,
        context: {
          ...context,
          headers: headersWithAuth,
        },
        fetchPolicy: isServer() ? 'no-cache' : fetchPolicy,
        errorPolicy: 'all',
      };
      let q = {};
      if (type && type === 'mutation') {
        requestConfig.mutation = query;
        q = this.mutate(requestConfig);
      } else {
        requestConfig.query = query;
        q = this.query(requestConfig);
      }
      q.then((data) => {
        const queryDuration = getDateObject().getTime() - startTime;
        if (enableGqlWarn && gqlWarnThreshold < queryDuration) {
          logger.info(
            `[SLOW-API] GraphQL slowness detected. Took ${queryDuration}msec to process below query:${rawQuery}`
          );
        }
        if (isServer()) {
          logger.info(
            `[Node Performance] Query ${getOperationName(query)}-${
              query.definitions[0]?.selectionSet?.selections[0]?.name?.value
            } executed in ${queryDuration}ms`
          );
        }
        if (handleError && data.errors && data.errors.length) {
          const gqlError = getGraphQLErrorOnSuccess(data);
          const operationName = getOperationName(query);
          logger.error(`Error in GraphQL - On Success - ${operationName}`, {
            error: `Error in GraphQL - On Success - ${operationName}`,
            extraData: {
              query: rawQuery,
              headers: headersWithAuth,
              cache: cache?.data?.data, // cache.data has added lot of new methods as per v3 cache object, while we just need cached query object.
              gqlError,
            },
            errorTags: [operationName],
          });
          const { errorCode } = data.errors[0].extensions || {};
          const storeRef = getStoreRef();
          if (isLogOutReq(storeRef, errorCode, operationName)) {
            const payload = { fromClient: true };
            storeRef.dispatch(logout(payload));
          }
          // checkout service error handling
          if (
            GRAPHQL_EXCEPTIONS.CHECKOUT_ERROR_MESSAGES.includes(errorCode) &&
            storeRef &&
            storeRef !== null
          ) {
            storeRef.dispatch(resetCartCache());
            window.location.pathname = `${getSiteId()}/bag`;
          }
          reject(gqlError);
        }
        if (updateQuery) {
          updateQuery(cache, data?.data, callbackParams);
        }
        if (newClient && fetchPolicy === FETCH_POLICY.NETWORK_ONLY && TTL && !data.errors) {
          updateCachedKeyMap(cacheConfigKey);
        }
        resolve(data);
      }).catch((e) => {
        const gqlError = getGraphQLErrorOnError(e);
        const operationName = getOperationName(query);
        logger.error(`Error in GraphQL - Network Error - ${operationName}`, {
          error: e,
          extraData: {
            query: rawQuery,
            headers: headersWithAuth,
            cache: cache?.data?.data, // cache.data has added lot of new methods as per v3 cache object, while we just need cached query object.
            gqlError,
          },
          errorTags: [operationName],
        });
        reject(gqlError);
      });
    });
  };
}

export default ApolloClientBase;
